import { Component, OnInit, inject, computed, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  AUDIT_TRIGGER_SOURCES,
  AuthStore,
  encodeApiError,
  PERMISSIONS,
  TApiErrorResponse,
} from '@sandbox-platform/shared-ui';
import { ExpertStore } from '../../store/expert.store';
import {
  PendingReferralTasksSectionComponent,
  QuickActionsSectionComponent,
  StatsSectionComponent,
} from './components';
import { ExpertApiService } from '../../services/expert-api.service';
import { CommissionInvoiceApiService } from '../../services/commission-invoice-api.service';
import { ReferralApiService } from '../../services/referral-api.service';
import { ReferralDTO, ReferralSearchDTO } from '../../types/referral.types';
import {
  buildNeedsRefereeAcceptanceSearchParams,
  buildWaitingOnRefereeSearchParams,
  hasMorePendingReferralTaskPages,
  PendingCommissionTask,
  PendingReferralTask,
  toPendingCommissionTasks,
  toPendingReferralTasks,
} from './pending-referral-tasks.definitions';
import {
  CommissionInvoiceDashboardSummaryDTO,
  PendingCommissionDTO,
} from '../../types';

@Component({
  standalone: true,
  imports: [
    StatsSectionComponent,
    PendingReferralTasksSectionComponent,
    QuickActionsSectionComponent,
  ],
  selector: 'lib-expert-dashboard',
  templateUrl: './expert-dashboard.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 100%;
      }

      /* Fade-in animation for smooth loading */
      .fade-in {
        opacity: 0;
        animation: fadeIn 0.6s ease-out forwards;
      }

      .fade-in-delay-1 {
        animation-delay: 0.2s;
      }

      .fade-in-delay-2 {
        animation-delay: 0.4s;
      }

      .fade-in-delay-3 {
        animation-delay: 0.6s;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Skeleton shimmer animation */
      .shimmer {
        background: linear-gradient(
          90deg,
          #f0f0f0 25%,
          #e0e0e0 50%,
          #f0f0f0 75%
        );
        background-size: 200% 100%;
        animation: shimmer 2s infinite;
      }

      @keyframes shimmer {
        0% {
          background-position: -200% 0;
        }
        100% {
          background-position: 200% 0;
        }
      }
    `,
  ],
})
export class ExpertDashboardComponent implements OnInit {
  protected readonly authStore = inject(AuthStore);
  protected readonly expertStore = inject(ExpertStore);
  private readonly expertApiService = inject(ExpertApiService);
  private readonly referralApiService = inject(ReferralApiService);
  private readonly commissionInvoiceApiService = inject(
    CommissionInvoiceApiService
  );

  // Local loading states for progressive loading
  protected isInitializing = signal(true);
  protected hasInitialized = signal(false);
  protected pendingReferralTasksLoading = signal(false);
  protected pendingCommissionTasksLoading = signal(false);
  protected pendingReferralTasksError = signal<
    string | TApiErrorResponse | null
  >(null);
  protected invoiceSummaryLoading = signal(false);
  protected invoiceSummaryError = signal<string | TApiErrorResponse | null>(
    null
  );
  protected needsRefereeAcceptanceTasks = signal<PendingReferralTask[]>([]);
  protected waitingOnRefereeTasks = signal<PendingReferralTask[]>([]);
  protected commissionDueTasks = signal<PendingCommissionTask[]>([]);
  protected invoiceSummary =
    signal<CommissionInvoiceDashboardSummaryDTO | null>(null);

  // Computed property for stats state to pass to stats section
  protected statsState = computed(() => ({
    ...this.expertStore.stats(),
    error: this.expertStore.stats().error ?? this.invoiceSummaryError(),
    invoiceSummary: this.invoiceSummary(),
    invoiceSummaryLoading: this.invoiceSummaryLoading(),
  }));

  // Computed property for overall loading state
  protected isLoading = computed(
    () => this.isInitializing() || this.expertStore.stats().loading
  );

  private readonly statsPermissionList = [
    PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_TOTAL_READ,
    PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_ACTIVE_READ,
    PERMISSIONS.EXPERT.DASHBOARD_ATTENDANCE_PRESENT_READ,
  ];

  private readonly dashboardSectionPermissionList = [
    ...this.statsPermissionList,
    PERMISSIONS.EXPERT.DASHBOARD_COMPANY_TOTALS_READ,
    PERMISSIONS.EXPERT.COMMISSION_INVOICE_READ,
    PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE,
    PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE_ANY,
  ];

  protected readonly canLoadExpertStats = computed(() =>
    this.authStore.hasAnyExpertPermission(this.statsPermissionList)
  );

  protected readonly canViewStats = computed(() =>
    this.authStore.hasAnyExpertPermission(this.dashboardSectionPermissionList)
  );

  private readonly quickActionPermissionList = [
    PERMISSIONS.EXPERT.EXPERTS_CREATE,
    PERMISSIONS.EXPERT.EXPERTS_UPDATE,
    PERMISSIONS.EXPERT.ATTENDANCE_READ,
    PERMISSIONS.EXPERT.EXPERTS_READ,
    PERMISSIONS.EXPERT.REFERRAL_READ,
  ];

  protected readonly canViewQuickActions = computed(() =>
    this.authStore.hasAnyExpertPermission(this.quickActionPermissionList)
  );

  protected readonly canViewPendingReferralTasks = computed(() =>
    this.authStore.hasExpertPermission(PERMISSIONS.EXPERT.REFERRAL_READ)
  );

  protected readonly canViewPendingTasks = computed(
    () =>
      this.canViewPendingReferralTasks() || this.canViewPendingCommissionTasks()
  );

  protected readonly canViewPendingCommissionTasks = computed(() =>
    this.authStore.hasAnyExpertPermission([
      PERMISSIONS.EXPERT.DASHBOARD_COMPANY_TOTALS_READ,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_READ,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE,
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE_ANY,
    ])
  );

  protected readonly canViewInvoiceSummary = computed(() => {
    if (
      this.authStore.hasExpertPermission(
        PERMISSIONS.EXPERT.DASHBOARD_COMPANY_TOTALS_READ
      )
    ) {
      return true;
    }

    return (
      this.authStore.isCurrentUserExpert() &&
      this.authStore.hasAnyExpertPermission([
        PERMISSIONS.EXPERT.COMMISSION_INVOICE_READ,
        PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE,
        PERMISSIONS.EXPERT.COMMISSION_INVOICE_CREATE_ANY,
      ])
    );
  });

  // Computed property for showing content with staggered animation
  protected showContent = computed(
    () => this.hasInitialized() && !this.expertStore.stats().loading
  );

  ngOnInit() {
    this.initializeDashboard();
  }

  protected async initializeDashboard() {
    try {
      this.authStore.loadFromStorage();

      if (this.authStore.isLoggedIn()) {
        await this.loadInitialData();
      }
    } catch (error) {
      console.error(
        'ExpertDashboardComponent - initializeDashboard error:',
        error
      );
    } finally {
      this.isInitializing.set(false);
      setTimeout(() => {
        this.hasInitialized.set(true);
      }, 100);
    }
  }

  private async loadInitialData() {
    try {
      await Promise.all([
        this.canLoadExpertStats()
          ? this.expertStore.loadExpertStats()
          : Promise.resolve(),
        this.loadPendingReferralTasks(),
        this.loadInvoiceDashboardSummary(),
        this.loadPendingCommissionTasks(),
      ]);
    } catch (error) {
      console.error('ExpertDashboardComponent - loadInitialData error:', error);
      // Don't rethrow - let the component show with error states
    }
  }

  private async loadPendingReferralTasks(): Promise<void> {
    if (!this.canViewPendingReferralTasks()) {
      this.resetPendingReferralTasks();
      return;
    }

    const currentExpertId = this.authStore.currentUserExpertId();
    if (!currentExpertId) {
      this.resetPendingReferralTasks();
      return;
    }

    this.pendingReferralTasksLoading.set(true);
    this.pendingReferralTasksError.set(null);

    try {
      const currentExpertResponse = await firstValueFrom(
        this.expertApiService.getById(
          currentExpertId,
          AUDIT_TRIGGER_SOURCES.DETAIL
        )
      );
      const organizationId = currentExpertResponse.data?.organization_id;

      if (!organizationId) {
        this.resetPendingReferralTasks();
        return;
      }

      const [needsAcceptance, waitingOnReferee] = await Promise.all([
        this.loadAllPendingReferrals((page) =>
          buildNeedsRefereeAcceptanceSearchParams(organizationId, page)
        ),
        this.loadAllPendingReferrals((page) =>
          buildWaitingOnRefereeSearchParams(organizationId, page)
        ),
      ]);

      this.needsRefereeAcceptanceTasks.set(
        toPendingReferralTasks('needs_referee_acceptance', needsAcceptance)
      );
      this.waitingOnRefereeTasks.set(
        toPendingReferralTasks('waiting_on_referee', waitingOnReferee)
      );
    } catch (error) {
      this.pendingReferralTasksError.set(
        encodeApiError(error, 'Failed to load pending referral tasks')
      );
      this.needsRefereeAcceptanceTasks.set([]);
      this.waitingOnRefereeTasks.set([]);
    } finally {
      this.pendingReferralTasksLoading.set(false);
    }
  }

  private async loadInvoiceDashboardSummary(): Promise<void> {
    if (!this.canViewInvoiceSummary()) {
      this.invoiceSummary.set(null);
      this.invoiceSummaryError.set(null);
      return;
    }

    this.invoiceSummaryLoading.set(true);
    this.invoiceSummaryError.set(null);

    try {
      const response = await firstValueFrom(
        this.commissionInvoiceApiService.dashboardSummary()
      );
      this.invoiceSummary.set(response.data ?? null);
    } catch (error) {
      this.invoiceSummary.set(null);
      this.invoiceSummaryError.set(
        encodeApiError(error, 'Failed to load invoice totals')
      );
    } finally {
      this.invoiceSummaryLoading.set(false);
    }
  }

  private async loadPendingCommissionTasks(): Promise<void> {
    if (!this.canViewPendingCommissionTasks()) {
      this.commissionDueTasks.set([]);
      return;
    }

    try {
      this.pendingCommissionTasksLoading.set(true);
      const response = await firstValueFrom(
        this.commissionInvoiceApiService.pendingCommissions()
      );
      const commissions: PendingCommissionDTO[] = response.data?.items ?? [];
      this.commissionDueTasks.set(toPendingCommissionTasks(commissions));
    } catch (error) {
      this.pendingReferralTasksError.set(
        encodeApiError(error, 'Failed to load pending commission tasks')
      );
      this.commissionDueTasks.set([]);
    } finally {
      this.pendingCommissionTasksLoading.set(false);
    }
  }

  private async loadAllPendingReferrals(
    buildParams: (page: number) => ReferralSearchDTO
  ): Promise<ReferralDTO[]> {
    const referrals: ReferralDTO[] = [];
    let page = 1;
    let total = 0;

    do {
      const response = await firstValueFrom(
        this.referralApiService.get(
          buildParams(page),
          AUDIT_TRIGGER_SOURCES.LIST
        )
      );
      const data = response.data;
      const items = data?.items ?? [];

      referrals.push(...items);
      total = data?.total ?? referrals.length;
      page += 1;
    } while (hasMorePendingReferralTaskPages(referrals.length, total, page));

    return referrals;
  }

  private resetPendingReferralTasks(): void {
    this.pendingReferralTasksError.set(null);
    this.needsRefereeAcceptanceTasks.set([]);
    this.waitingOnRefereeTasks.set([]);
  }
}
